import { Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  ApiCallingV2Service,
  ConfirmHelperService,
  CurrencyService,
  DropdownData,
  rxResourceFixed
} from '@zayan-erp/shared';
import { selectProjects } from '@zayan-erp/stores';
import { DsmApsService } from '../../services/dsm-aps.service';
import { DsmApsDealContextService } from '../../services/dsm-aps-deal-context.service';
import { DsmDepositService } from '../../services/dsm-deposit.service';
import { DsmProjectDepositScheduleService } from '../../services/dsm-project-deposit-schedule.service';
import { DsmCreateDeposit, DsmCreateDepositPayment, DsmImportDeposit } from '../../models/dsm-deposit.models';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { DsmApsDepositsFinancialSummaryComponent } from './dsm-aps-deposits-financial-summary.component';
import { DsmApsDepositsImportsComponent } from './dsm-aps-deposits-imports.component';
import { DsmApsDepositsTableComponent } from './dsm-aps-deposits-table.component';
@Component({
  selector: 'dsm-aps-deposits',
  standalone: true,
  imports: [
    DsmApsDepositsFinancialSummaryComponent,
    DsmApsDepositsTableComponent,
    DsmApsDepositsImportsComponent,
    DialogModule,
    ButtonModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  host: { class: 'flex flex-col flex-1 overflow-hidden' },
  templateUrl: './dsm-aps-deposits.component.html'
})
export class DsmApsDepositsComponent {
  dealId = input.required<string>();
  /** Set inside an addendum's "Deposits and Payments" window: only that addendum's deposits, no summary. */
  addendumId = input<string | null>(null);
  /** The lot to read when no deal is in context (the spec-lot addendum view). */
  lotId = input<string | null>(null);
  /** After any deposit or payment is saved or deleted. */
  changed = output<void>();

  #currencyService = inject(CurrencyService);

  #depositService = inject(DsmDepositService);
  #confirmationService = inject(ConfirmationService);
  #confirmHelper = inject(ConfirmHelperService);
  #api = inject(ApiCallingV2Service);
  #store = inject(Store);
  #depositScheduleService = inject(DsmProjectDepositScheduleService);
  #dsmApsService = inject(DsmApsService);
  #dealContext = inject(DsmApsDealContextService);
  #destroyRef = inject(DestroyRef);

  importType = signal<string>('lots');
  importsDialogVisible = signal(false);

  projects$ = this.#store.select(selectProjects);
  projects = toSignal(this.projects$, { initialValue: [] });

  dealResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.dealId) return of(null);
      return this.#dsmApsService.getDealHeader(params.dealId);
    }
  });

  effectiveDeal = computed(() => this.#dealContext.currentDeal() ?? this.dealResource.value() ?? null);
  effectiveLotId = computed(() => this.lotId() || this.effectiveDeal()?.lot?.id || '');

  // Read by deal only for the lot being shown: the shared deal context can hold a deal from earlier navigation.
  readDealId = computed(() => {
    const deal = this.effectiveDeal();
    if (!deal?.id) return '';
    return !this.lotId() || deal.lot?.id === this.lotId() ? deal.id : '';
  });

  constructor() {
    effect(() => {
      const deal = this.dealResource.value();
      if (deal) {
        this.#dealContext.currentDeal.set(deal);
      }
      this.#depositService.dealId.set(this.effectiveDeal()?.id ?? '');
      this.#depositService.lotId.set(this.effectiveLotId());
    });

    effect(() => {
      if (this.#dealContext.reloadDeal()) {
        this.dealResource.reload();
        this.#dealContext.reloadDeal.set(false);
      }
    });
  }

  getProjectId = rxResourceFixed({
    params: () => ({ dealId: this.effectiveDeal()?.id ?? '' }),
    defaultValue: '',
    stream: ({ params }) => {
      if (!params.dealId) {
        return of('');
      }
      const currentDeal = this.effectiveDeal();
      const projectCode = currentDeal?.project?.projectCode;
      if (!projectCode) return of('');
      const project = this.projects().find((p: DropdownData) => p.firstLabel === projectCode);
      return of(project?.id ?? '');
    }
  });

  projectId = computed(() => this.getProjectId.value() ?? '');

  // By deal when there is one (a lot can still hold another deal's leftovers); spec lots with no deal read by lot.
  getDeposits = rxResourceFixed({
    params: () => ({ dealId: this.readDealId(), lotId: this.effectiveLotId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (params.dealId) {
        return this.#depositService.readDepositsByDealId(params.dealId);
      }
      if (!params.lotId) {
        return of([]);
      }
      return this.#depositService.readDepositsByLotId(params.lotId);
    }
  });

  deposits = computed(() => {
    const all = this.getDeposits.value() ?? [];
    const addendumId = this.addendumId();
    return addendumId ? all.filter(deposit => deposit.addendumId === addendumId) : all;
  });

  reload(): void {
    this.getDeposits.reload();
    this.getSummary.reload();
  }

  getSummary = rxResourceFixed({
    params: () => ({ dealId: this.readDealId(), lotId: this.effectiveLotId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (params.dealId) {
        return this.#depositService.getSummaryByDeal(params.dealId);
      }
      if (!params.lotId) {
        return of([]);
      }
      return this.#depositService.getSummary(params.lotId);
    }
  });

  summary = computed(() => this.getSummary.value() ?? []);

  onDepositFormSubmitted(): void {
    this.#depositService.form.markAllAsTouched();
    if (this.#depositService.form.invalid) {
      return;
    }

    this.#depositService.form.patchValue({
      // null, not '': the API binds dealId/lotId as Guid? and rejects an empty string (spec lots have no deal).
      dealId: this.effectiveDeal()?.id ?? null,
      lotId: this.effectiveLotId() || null
    });

    if (this.#depositService.depositId) {
      this.#depositService
        .updateDeposit(this.#depositService.depositId, this.#depositService.form.getRawValue() as never)
        .subscribe({
          next: () => {
            this.reset();
            this.#dealContext.reloadDeal.set(true);
          }
        });
    } else {
      this.#depositService.createDeposit([this.#depositService.form.getRawValue()] as DsmCreateDeposit[]).subscribe({
        next: () => {
          this.reset();
          this.#dealContext.reloadDeal.set(true);
        }
      });
    }
  }

  onDepositPaymentFormSubmitted(event: { depositId: string; paymentId?: string }): void {
    this.#depositService.depositPaymentForm.markAllAsTouched();
    if (this.#depositService.depositPaymentForm.invalid) {
      return;
    }

    const { depositId, paymentId } = event;

    if (paymentId) {
      this.#depositService
        .updateDepositPayment(
          depositId,
          paymentId,
          this.#depositService.depositPaymentForm.getRawValue() as DsmCreateDepositPayment
        )
        .subscribe({
          next: () => this.reset()
        });
    } else {
      this.#depositService.depositPaymentForm.patchValue({ depositId });
      this.#depositService
        .createDepositPayment(
          depositId,
          this.#depositService.depositPaymentForm.getRawValue() as DsmCreateDepositPayment
        )
        .subscribe({
          next: () => this.reset()
        });
    }
  }

  reset(): void {
    this.#depositService.depositPaymentForm.reset();
    this.getDeposits.reload();
    this.#depositService.form.reset();
    this.#depositService.addPayment = false;
    this.#depositService.depositId = null;
    this.#depositService.paymentId = null;
    this.#depositService.addNewDeposits = false;
    this.#depositService.deposits.set([]);
    this.getSummary.reload();
    this.changed.emit();
  }

  onDeleteDeposit(depositId: string): void {
    this.#depositService.deleteDeposit(depositId).subscribe({
      next: () => this.reset()
    });
  }

  onBulkDeleteDeposits(depositIds: string[]): void {
    if (!depositIds.length) return;
    const deleteObservables = depositIds.map(id => this.#depositService.deleteDeposit(id));
    forkJoin(deleteObservables).subscribe({
      next: () => this.reset()
    });
  }

  onDeleteDepositPayment(paymentId: string): void {
    this.#depositService.deleteDepositPayment(paymentId).subscribe({
      next: () => this.reset()
    });
  }

  onConfirmDeleteDeposit(): void {
    const depositId = this.#depositService.depositId;
    const payments = this.deposits().find(deposit => deposit.id === depositId)?.payments ?? [];
    const paidTotal = payments.reduce((sum, payment) => sum + payment.paymentAmount, 0);

    this.#confirmationService.confirm({
      header: 'Delete Deposit',
      message: payments.length
        ? `This deposit has ${payments.length} payment${payments.length === 1 ? '' : 's'} totalling ${this.#currencyService.formatCurrency(paidTotal)}. Delete the deposit and all its payments?`
        : 'Are you sure you want to delete this deposit?',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonProps: { severity: 'danger' },
      rejectButtonProps: { severity: 'secondary', outlined: false },
      accept: () => {
        if (!depositId) return;
        const request = payments.length
          ? this.#depositService.deleteDepositWithPayments(depositId)
          : this.#depositService.deleteDeposit(depositId);
        request.subscribe({ next: () => this.reset() });
      }
    });
  }

  onConfirmDeleteDepositPayment(): void {
    this.#confirmationService.confirm({
      header: 'Delete Deposit Payment',
      message: 'Are you sure you want to delete this deposit payment?',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonProps: { severity: 'danger' },
      rejectButtonProps: { severity: 'secondary', outlined: false },
      accept: () => {
        if (this.#depositService.paymentId) {
          this.onDeleteDepositPayment(this.#depositService.paymentId);
        }
      }
    });
  }

  closeImportsDialog(): void {
    this.#depositService.deposits.set([]);
    this.importsDialogVisible.set(false);
    this.getDeposits.reload();
  }

  openImportsModal(importType: string | null): void {
    this.importType.set(importType ?? 'lots');
    if (importType === 'projects') {
      this.getProjectId.reload();
      this.#loadProjectDeposits().pipe(take(1), takeUntilDestroyed(this.#destroyRef)).subscribe();
    } else {
      this.importsDialogVisible.set(true);
    }
  }

  #resolveProjectIdForImport(): string {
    const currentDeal = this.effectiveDeal();
    const projectCode = currentDeal?.project?.projectCode;
    if (!projectCode) return '';
    const project = this.projects().find((p: DropdownData) => p.firstLabel === projectCode);
    return project?.id ?? '';
  }

  #loadProjectDeposits() {
    return of(this.#resolveProjectIdForImport()).pipe(
      switchMap(projectId => {
        if (!projectId) return of(null);
        return this.#depositScheduleService.getDepositsByProject(projectId).pipe(
          map(data => {
            const schedules = Array.isArray(data) ? data : [data];
            const firstRow = schedules[0]?.rows?.[0] as { depositScheduleId?: string } | undefined;
            return { projectId, depositScheduleId: firstRow?.depositScheduleId ?? '' };
          })
        );
      }),
      switchMap(result => {
        if (!result?.projectId || !result.depositScheduleId) return of(null);
        const generate = (
          confirmDuplicate: boolean
        ): Observable<{ projectId: string; depositScheduleId: string; data: DsmImportDeposit[] } | null> =>
          this.#depositService
            .generateDeposits(
              result.projectId,
              this.effectiveLotId() ?? '',
              this.effectiveDeal()?.id ?? '',
              result.depositScheduleId,
              confirmDuplicate
            )
            .pipe(
              map(data => ({ ...result, data })),
              catchError((error: HttpErrorResponse) => {
                if (error.status !== 409 || confirmDuplicate) {
                  this.#api.showMessage(
                    error.error?.errors?.[0] ?? error.error?.message ?? 'Could not generate the deposit schedule.',
                    'error'
                  );
                  return of(null);
                }
                return this.#confirmHelper
                  .showModal({
                    icon: 'confirm-icon',
                    header: 'Generate another schedule?',
                    message: 'This deal already has deposits. Generating the schedule again adds a second set.',
                    acceptLabel: 'Generate',
                    acceptSeverity: 'primary',
                    rejectLabel: 'Cancel'
                  })
                  .pipe(switchMap(confirmed => (confirmed ? generate(true) : of(null))));
              })
            );
        return generate(false);
      }),
      take(1),
      map(result => {
        if (!result?.data) return;
        const raw =
          (Array.isArray(result.data)
            ? result.data
            : (result.data as { depositPreviews?: unknown[] })?.depositPreviews) ?? [];
        const deposits: DsmImportDeposit[] = (
          raw as Array<{
            depositTypeId: string;
            depositTypeName: string;
            amount: number;
            calculatedDueDate?: string;
            baseDate?: string;
            taskId: string;
            depositScheduleRowId?: string;
            offsetBusinessDays?: number;
            sortOrder?: number;
          }>
        ).map(row => {
          const dueDateStr = row.calculatedDueDate ?? row.baseDate ?? '';
          return {
            lotId: this.effectiveLotId() ?? '',
            dealId: this.effectiveDeal()?.id ?? '',
            addendumId: '',
            taskId: row.taskId,
            depositType: {
              id: (result.data as { depositTypeId?: string })?.depositTypeId ?? '',
              name: (result.data as { depositTypeName?: string })?.depositTypeName ?? ''
            },
            depositDueDate: dueDateStr ? new Date(dueDateStr) : (null as unknown as string),
            depositAmount: row.amount ?? 0,
            notes: '',
            depositScheduleRowId: row.depositScheduleRowId,
            offsetBusinessDays: row.offsetBusinessDays,
            sortOrder: row.sortOrder
          } as unknown as DsmImportDeposit;
        });
        this.#depositService.deposits.set(deposits);
        this.getDeposits.reload();
      })
    );
  }

  onSelectedLot(lotId: string): void {
    this.#depositService.earnestByLotId(lotId).subscribe({
      next: (data: DsmImportDeposit[]) => {
        const depositsWithDates = data.map((deposit: DsmImportDeposit & { depositType: { id: string } }) => ({
          ...deposit,
          depositDueDate: deposit.depositDueDate ? new Date(deposit.depositDueDate as string) : null,
          depositTypeId: deposit.depositType.id
        }));
        this.#depositService.deposits.set(depositsWithDates as unknown as DsmImportDeposit[]);
      }
    });
  }

  onImportData(): void {
    const deposits = this.#depositService.deposits();
    const depositsToSave = deposits.map((deposit: DsmImportDeposit & { depositType: { id: string } }) => ({
      lotId: deposit.lotId,
      dealId: this.effectiveDeal()?.id ?? '',
      addendumId: deposit.addendumId,
      taskId: deposit.taskId,
      depositTypeId: deposit.depositType.id,
      depositDueDate: deposit.depositDueDate,
      depositAmount: deposit.depositAmount,
      notes: deposit.notes
    }));

    this.#depositService.createDeposit(depositsToSave as unknown as DsmCreateDeposit[]).subscribe({
      next: () => {
        this.reset();
        this.closeImportsDialog();
      }
    });
  }

  onImportDataFromProjects = rxResourceFixed({
    params: () => ({ projectId: this.projectId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.projectId) {
        return of([]);
      }
      return this.#depositScheduleService.getDepositsByProject(params.projectId);
    }
  });

  depositScheduleId = computed(() => {
    const data = this.onImportDataFromProjects.value();
    if (data == null) return '';
    const schedules = Array.isArray(data) ? data : [data];
    const firstRow = schedules[0]?.rows?.[0] as { depositScheduleId?: string } | undefined;
    return firstRow?.depositScheduleId ?? '';
  });
}
